import fromExponential from 'from-exponential'

export const roundedDecimalPlaces = {
    ETH: 8,
    BTC: 8,
    WBTC: 8,
    USDT: 6,
    USDC: 6,
    SOL: 9,
    USDU: 4,
    UNTB: 4,
    EOS: 4,
    ATRI: 0,
    XRP: 6,
    XAU: 3,
    XAG: 3,
    XPT: 3,
    VET: 8,
    DOGE: 8,
    SHIB: 8,
    RLX: 8,
    ADA: 6,
    PAUG: 6,
    PAUX: 6,
    FTM: 8,
    CRU: 4,
    WCRU: 4,
    DWRLD: 4,
    TRX: 6,
    AIR777: 4,
    MT777: 4,
    MT760: 4,
    GXAG: 6,
    LIF3: 8,
}

export const decimalPlaces = {
    ...roundedDecimalPlaces,
    ETH: 18,
    VET: 18,
    SHIB: 18,
    RLX: 18,
    FTM: 18,
    LIF3: 18,
}

export const dateAndTimeToLocaleString = date =>
    new Date(date).toLocaleString('en-US', {
        hourCycle: 'h23',
        hour: '2-digit',
        minute: '2-digit',
        year: 'numeric',
        month: 'short',
        day: '2-digit',
    })

export const dateAndTimeToLocaleStringNoYear = date =>
    new Date(date).toLocaleString('en-US', { hourCycle: 'h23', hour: '2-digit', minute: '2-digit', month: 'short', day: '2-digit' })

export const dateToLocaleString = date =>
    new Date(date).toLocaleString('en-US', { hourCycle: 'h23', year: 'numeric', month: 'short', day: '2-digit' })

export const toLocaleString = (value, currencyCode) =>
    parseFloat(value).toLocaleString('en-US', {
        minimumFractionDigits: roundedDecimalPlaces[currencyCode] ?? 2,
        maximumFractionDigits: roundedDecimalPlaces[currencyCode] ?? 2,
    })

// TODO: Replace toLocaleStringWithCurrency with formatMoney with object params
export function formatMoney(amount, { currency = 'EUR', precision = 2 } = {}) {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
    }).format(amount)
}

export const toLocaleStringWithCurrency = (value, currencyCode, noDecimal) => {
    if (!currencyCode) {
        return ''
    } //Fall-back for misconfigured currencies if any
    if (roundedDecimalPlaces[currencyCode] === 0 || roundedDecimalPlaces[currencyCode]) {
        const number = parseFloat(value).toLocaleString('en-US', {
            minimumFractionDigits: noDecimal ? 0 : roundedDecimalPlaces[currencyCode] ?? 2,
            maximumFractionDigits: noDecimal ? 0 : roundedDecimalPlaces[currencyCode] ?? 2,
        })

        return `${number}${' '}${currencyCode}`
    }

    if (currencyCode === 'CHF') {
        return Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'CHF',
            currencyDisplay: 'code',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })
            .format(value)
            .replace('CHF', 'Fr')
    }

    return parseFloat(value).toLocaleString('en-US', {
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: 'symbol',
        minimumFractionDigits: noDecimal ? 0 : 2,
        maximumFractionDigits: noDecimal ? 0 : 2,
    })
}

export const toLocaleStringNoCurrencySymbol = (value, currencyCode) => {
    if (!currencyCode) {
        return ''
    } //Fall-back for misconfigured currencies if any
    if (roundedDecimalPlaces[currencyCode] === 0 || roundedDecimalPlaces[currencyCode]) {
        const number = parseFloat(value).toLocaleString('en-US', {
            minimumFractionDigits: roundedDecimalPlaces[currencyCode] ?? 2,
            maximumFractionDigits: roundedDecimalPlaces[currencyCode] ?? 2,
        })

        return `${number}${' '}${currencyCode}`
    }

    return `${parseFloat(value).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })} ${currencyCode}`
}

export const toLocaleStringWithCurrencyNoDecimals = (value, currencyCode) => {
    if (roundedDecimalPlaces[currencyCode] === 0 || roundedDecimalPlaces[currencyCode]) {
        const number = parseFloat(value).toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        })

        return `${number}${' '}${currencyCode}`
    }
    if (currencyCode === 'CHF') {
        return Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'CHF',
            currencyDisplay: 'code',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        })
            .format(value)
            .replace('CHF', 'Fr')
    }

    return parseFloat(value).toLocaleString('en-US', {
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: 'symbol',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    })
}

export const toLocaleStringWithCurrencyExchRate = (value, currencyCode) => {
    if (roundedDecimalPlaces[currencyCode] === 0 || roundedDecimalPlaces[currencyCode]) {
        const number = parseFloat(value).toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 7,
        })

        return `${number}${' '}${currencyCode}`
    }
    if (currencyCode === 'CHF') {
        return Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'CHF',
            currencyDisplay: 'code',
            minimumFractionDigits: 0,
            maximumFractionDigits: 7,
        })
            .format(value)
            .replace('CHF', 'Fr')
    }

    return parseFloat(value).toLocaleString('en-US', {
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: 'symbol',
        minimumFractionDigits: 0,
        maximumFractionDigits: 7,
    })
}

export const toLocaleStringWithCurrencyNoDecimalRounding = (value, currencyCode) =>
    parseFloat(value).toLocaleString('en-US', {
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: 'symbol',
        minimumFractionDigits: 0,
        maximumFractionDigits: 8,
    })

export const toLocaleStringWithCurrencyWithHigherNumbers = (value, currencyCode) => {
    if (decimalPlaces[currencyCode] === 0 || decimalPlaces[currencyCode]) {
        const number = parseFloat(value).toLocaleString('en-US', {
            minimumFractionDigits: currencyCode === 'ETH' ? roundedDecimalPlaces[currencyCode] : decimalPlaces[currencyCode] ?? 2,
            maximumFractionDigits: currencyCode === 'ETH' ? roundedDecimalPlaces[currencyCode] : decimalPlaces[currencyCode] ?? 2,
        })

        return `${number}${' '}${currencyCode}`
    }

    if (currencyCode === 'CHF') {
        return Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'CHF',
            currencyDisplay: 'code',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })
            .format(value)
            .replace('CHF', 'Fr')
    }

    return parseFloat(value).toLocaleString('en-US', {
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: 'symbol',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })
}

export const roundToFixed = (value, currencyCode) => {
    const decimals = Math.pow(10, decimalPlaces[currencyCode] || 2)

    return fromExponential(Math.floor(parseFloat(value) * decimals) / decimals)
    // return parseFloat(parseFloat(value)).toFixed(fixedNumber);
}

export const truncateNumber = (value, truncateTo, currencyType) => {
    if (currencyType) {
        return Math.round((value + Number.EPSILON) * 100) / 100
    }
    if (truncateTo) {
        return parseFloat(`${value}`.substr(0, truncateTo))
    }
}

export const filterItemsByType = (type, list) => list.filter(item => item.type === type)

/**
 * Formats improper amount by allowing only numbers and commas.
 *
 * *Note:* this also allows multiple commas which user should replace by themselves.
 * @param {String} str amount
 * @returns formatted amount string.
 */
export const formatImproperAmount = (str, allowComma) => {
    if (typeof str !== 'string') {
        throw new TypeError('Expected a string')
    }
    if (allowComma) {
        return str.replace(/,/g, '.').replace(/[^0-9\\.]/g, '')
    }

    return str.replace(/[^0-9]/g, '')
}

/**
 * If amount is not a number, returns empty string.
 * @param {String} values amount
 * @returns formatted amount string.
 */
export const isInfinity = values => (isFinite(Number(values)) ? values : '')

/**
 * Mask IBAN for SWIFT transfers.
 */
export const maskIbanForSwift = (accountNumber, isSwift) => {
    if (accountNumber && isSwift) {
        return `${accountNumber?.substring(0, 2)}**${accountNumber?.substring(accountNumber?.length - 4)}`
    }

    return accountNumber || ''
}
